import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import TextArea from '../../../../components/TextArea';

import { NotePayload } from '../../../../models/requestModels.ts/note';

import { setGlobalSave } from '../../../../store/reducers/globalSaveReducer';
import {
	useAddOrUpdateNotesMutation,
	useGetNotesQuery,
} from '../../../../store/apiSlices/notesApiSlice';
import { RootState } from '../../../../store/store';

import plus from '../../../../assets/icons/plus.svg';
import doneSvg from '../../../../assets/icons/done.svg';
import editIcon from '../../../../assets/icons/edit.svg';

import { FormikRefs } from '../../../../constants';
import { toastHandler } from '../../../../utils/toastHandler';
import { getInitials } from '../../../../functions/stringFunctions';
import { getFormattedDateString } from './../../../../functions/dateFunction';

import { useFormikRefs } from '../../../../hooks/useFormikRef';
import { setNotesProperties } from '../../../../store/reducers/notesReducer';
import useResetComponent from '../../../../hooks/useComponentReset';
import { setDirty } from '../../../../store/reducers/unSavedChangesReducer';
import { useGetRecruitDetailsByIdQuery } from '../../../../store/apiSlices/recruitApiSlice';
import { StatusCode } from '../../../../enums/httpStatus.enum';

const Notes = () => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const { registerRef, unregisterRef, updateRefs } = useFormikRefs();
	const recruit_id = searchParams.get('id');
	const formKey = FormikRefs.notesFormikRef;
	const globalSave = useSelector((state: RootState) => state.globalSave);
	const user = useSelector((store: RootState) => store.user);
	const notes = useSelector((state: RootState) => state.notesReducer);
	const unSavedChanges = useSelector(
		(store: RootState) => store.unSavedChangesReducer,
	);
	const recruitDetails = useGetRecruitDetailsByIdQuery({
		userId: recruit_id as string,
		userEmail: user.user_email,
	});
	const notesData = useGetNotesQuery({
		recruiterId: recruit_id ?? '',
		userEmail: user.user_email,
	});
	const [updateNotes, updateNotesProps] = useAddOrUpdateNotesMutation();

	useResetComponent({ reset: () => handleCancel() });

	const formik: FormikProps<NotePayload> = useFormik<NotePayload>({
		initialValues: {
			note_id: null,
			value: '',
			recruit_id: recruit_id!,
			user_email: user.user_email,
		},
		validationSchema: Yup.object({
			value: Yup.string().required('').min(1, 'Note should not be empty'),
		}),
		onSubmit: async (values) => {
			if (!values?.value?.trim()) return;
			dispatch(
				setGlobalSave({
					isGlobalSaveActivated: false,
					apisCalled: {
						...globalSave.apisCalled,
						isNotesAPICalled: true,
					},
				}),
			);
			dispatch(
				setNotesProperties({
					...notes,
					editableIndex: -1,
					addNotes: false,
				}),
			);
			try {
				toastHandler({
					isLoading: true,
					msg:
						notes.editableIndex == -1
							? 'Adding notes'
							: 'Updated notes',
				});
				const res = await updateNotes({
					...values,
					value: values?.value?.trim(),
				});
				if (res.data?.statusCode == StatusCode.SERVER_ERROR) {
					throw res.data.value as string;
				}
				if (res.data?.statusCode == StatusCode.UNAUTHORIZED) {
					throw res.data.message as string;
				}
				toastHandler({
					msg:
						'Successfully ' +
						(notes.editableIndex == -1
							? 'added notes'
							: 'updated notes'),
				});
				recruitDetails?.refetch?.();
			} catch {
				toastHandler({
					isError: true,
					msg:
						'Failed ' +
						(notes.editableIndex == -1
							? 'add notes'
							: 'update notes'),
				});
			}
			dispatch(
				setGlobalSave({
					isGlobalSaveActivated: false,
					apisCalled: {
						...globalSave.apisCalled,
						isNotesAPICalled: false,
					},
				}),
			);
			formik.resetForm({
				values: {
					note_id: null,
					value: '',
					recruit_id: recruit_id!,
					user_email: user.user_email,
				},
			});
		},
	});

	useEffect(() => {
		registerRef(formKey, { current: formik });

		return () => {
			unregisterRef(formKey);
		};
	}, [formKey, formik, registerRef, unregisterRef]);

	useEffect(() => {
		updateRefs?.();
		dispatch(
			setDirty({
				isDirty: formik.dirty || false,
				isNavigationAllowed: true,
				isModalOpen: false,
			}),
		);
	}, [formik.dirty]);

	const addNotes = () => {
		if (!user.roles.adminAppAddNotes) return;
		dispatch(
			setNotesProperties({
				...notes,
				editableIndex: -1,
				addNotes: true,
			}),
		);
		formik.resetForm({
			values: {
				value: '',
				recruit_id: recruit_id!,
				user_email: user.user_email,
			},
		});
	};

	useEffect(() => {
		if ((notesData.currentData?.value?.length ?? -1) === 0) {
			dispatch(
				setNotesProperties({
					...notes,
					addNotes: true,
				}),
			);
		}
	}, [notesData.isSuccess]);

	const handleDone = () => {
		if (!user.roles.adminAppAddNotes) return;
		formik.submitForm();
	};

	const handleCancel = () => {
		if (formik.dirty) {
			dispatch(
				setDirty({
					isDirty: formik.dirty,
					isModalOpen: true,
					isNavigationAllowed: false,
					handleConfirm: handleModalConfirm,
					handleCancel: handleModalCancel,
				}),
			);
		} else {
			handleModalConfirm();
		}
	};

	const handleModalCancel = () => {
		dispatch(
			setDirty({
				...unSavedChanges,
				isModalOpen: false,
			}),
		);
	};

	const handleModalConfirm = () => {
		dispatch(
			setNotesProperties({
				...notes,
				editableIndex: -1,
				addNotes: false,
			}),
		);
		formik.resetForm();
	};

	const handleEditDone = () => {
		formik.submitForm();
	};

	const handleEdit = (i: number) => {
		dispatch(
			setNotesProperties({
				...notes,
				editableIndex: i,
				addNotes: false,
			}),
		);
		const note = notesData?.currentData?.value[i];
		formik.resetForm({
			values: {
				note_id: note?.note_id,
				value: note?.value!,
				recruit_id: recruit_id!,
				user_email: user.user_email,
			},
		});
	};

	return (
		<div className="flex flex-col bg-white p-4 gap-y-4 rounded-lg overflow-auto">
			<div className="flex items-center justify-between">
				<div className="text-xl font-bold">Notes</div>
				<div className="flex items-center gap-x-4">
					{notes?.addNotes ? (
						<>
							<div className="flex">
								<p
									className="flex cursor-pointer"
									onClick={() => handleCancel()}
								>
									Cancel
								</p>
							</div>
							<div className="flex items-center gap-x-1">
								<img className="w-4 h-4" src={doneSvg} alt="" />
								<p
									className="flex cursor-pointer"
									onClick={() => handleDone()}
								>
									Done
								</p>
							</div>
						</>
					) : (
						<div className="flex items-center gap-x-1">
							<img className="w-4 h-4" src={plus} alt="" />
							<p
								className={`flex underline ${globalSave.apisCalled.isNotesAPICalled || globalSave.isGlobalSaveActivated ? '' : 'cursor-pointer'}`}
								onClick={() => {
									if (
										!(
											globalSave.apisCalled
												.isNotesAPICalled ||
											globalSave.isGlobalSaveActivated
										)
									) {
										addNotes();
									}
								}}
							>
								Add Notes
							</p>
						</div>
					)}
				</div>
			</div>
			{notes.addNotes ? (
				<TextArea placeholder="Add your notes here" formik={formik} />
			) : null}
			<div className="grid overflow-y-scroll max-h-[50vh] gap-4">
				{notesData.currentData?.value?.map((note, i) => {
					return (
						<div
							key={note.note_id}
							className={`${i < (notesData.currentData?.value.length || 0) - 1 && 'border-b-2 pb-3'}`}
						>
							{notes.editableIndex === i ? (
								<div className="flex flex-col">
									<div className="flex gap-x-4 justify-end items-center">
										<div className="flex">
											<p
												className="flex cursor-pointer"
												onClick={() => handleCancel()}
											>
												Cancel
											</p>
										</div>
										<div className="flex items-center gap-x-1">
											<img
												className="w-4 h-4"
												src={doneSvg}
												alt=""
											/>
											<p
												className="flex cursor-pointer"
												onClick={() => handleEditDone()}
											>
												Done
											</p>
										</div>
									</div>
									<TextArea placeholder="" formik={formik} />
								</div>
							) : (
								<div className="flex flex-col gap-y-2">
									<div className="flex justify-between">
										<div className="flex items-center gap-x-2 text-base font-medium">
											<div className="rounded-full w-fit p-1 bg-rcrm-primary-blue text-white">
												{getInitials(note.manager_name)}
											</div>
											{note.manager_name}
										</div>
										<p className="text-sm font-medium ">
											{note.is_edited ? 'Edited' : ''}
										</p>
									</div>
									<p className="break-all">{note.value}</p>
									<div className="flex justify-between">
										<p>
											<span className="text-xs">
												Last updated on{' '}
											</span>
											{getFormattedDateString(
												note.update_date,
											)}
										</p>
										{note.is_editable ? (
											<div
												className={`flex items-center underline text-sm font-medium 
												${globalSave.apisCalled.isNotesAPICalled || globalSave.isGlobalSaveActivated ? '' : 'cursor-pointer'} pr-2`}
												onClick={() => {
													if (
														!(
															globalSave
																.apisCalled
																.isNotesAPICalled ||
															globalSave.isGlobalSaveActivated
														)
													) {
														handleEdit(i);
													}
												}}
											>
												<img
													className="w-3 h-3"
													src={editIcon}
													alt=""
												/>
												{'Edit'}
											</div>
										) : (
											''
										)}
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};
export default Notes;
